import { GiCommercialAirplane } from 'react-icons/gi';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BsInstagram } from 'react-icons/bs';
import { FiFacebook, FiPhoneCall } from 'react-icons/fi';
import { BiTimeFive } from 'react-icons/bi';
import { IoClose, IoChevronDown } from 'react-icons/io5';
import Logo from '../../assets/logo_2x.png';
import { fetchDomesticPackages, fetchInternationalPackages, API_BASE_URL } from '../utils/api';
import BookNowModal from '../common/BookNowModal';

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isDomesticOpen, setIsDomesticOpen] = useState(false);
  const [isInternationalOpen, setIsInternationalOpen] = useState(false);
  const [domesticData, setDomesticData] = useState([]);
  const [internationalData, setInternationalData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [menuLoading, setMenuLoading] = useState(true);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setMenuLoading(true);
        const domestic = await fetchDomesticPackages({ type: 'category' });
        const international = await fetchInternationalPackages({ type: 'category' });
        setDomesticData(domestic.filter((item) => item.type === 'category')); // Filter for categories only
        setInternationalData(international.filter((item) => item.type === 'category')); // Filter for categories only
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setMenuLoading(false);
      }
    };
    fetchData();
  }, []);

  // Map domestic data to subLinks format
  const domesticSubLinks = domesticData.map((item) => ({
    name: item.name,
    path: `/domestic/${item.name.toLowerCase().replace(/\s+/g, '-')}`,
    icon: `${API_BASE_URL}/${item.icon}`, // Assuming icons are accessible via the URL provided
  }));

  // Map international data to subLinks format
  const internationalSubLinks = internationalData.map((item) => ({
    name: item.name,
    path: `/international/${item.name.toLowerCase().replace(/\s+/g, '-')}`,
    icon: `${API_BASE_URL}/${item.icon}`, // Assuming icons are accessible via the URL provided
  }));

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about-us' },
    {
      name: 'Domestic',
      path: '/domestic',
      subLinks: domesticSubLinks,
      hero: {
        title: 'Plan Your Indian Trip!',
        imageHeight: 'h-[160px]',
        text: 'Explore the diverse beauty of India with our curated Domestic Packages, from serene hill stations and pristine beaches to cultural and spiritual destinations. Enjoy hassle-free trips designed for comfort, and unforgettable memories.',
        linkText: 'Explore All Packages',
        linkPath: '/domestic',
        marginTop: 'mt-2',
      },
    },
    {
      name: 'International',
      path: '/international',
      subLinks: internationalSubLinks,
      hero: {
        title: 'Explore the World!',
        imageHeight: 'h-[180px]',
        text: 'Travel across continents and explore breathtaking beaches, iconic landmarks, cultural wonders, and thrilling adventures with our exclusive International Packages.',
        linkText: 'Explore All Packages',
        linkPath: '/international',
        marginTop: 'mt-2',
      },
    },
    {
      name: 'Services',
      path: '/services',
      subLinks: [
        {
          name: 'Book Hotel & Resorts',
          path: '/service/book-hotel-resorts',
          icon: require('../../assets/icon/HotelBook.svg').default,
        },
        {
          name: 'Holidays',
          path: '/service/holidays',
          icon: require('../../assets/icon/Holiday.svg').default,
        },
        {
          name: 'Flights',
          path: '/service/flights',
          icon: require('../../assets/icon/Flight.svg').default,
        },
        {
          name: 'Corporate Travels',
          path: '/service/corporate-travels',
          icon: require('../../assets/icon/CorporateTravels.svg').default,
        },
        {
          name: 'Travel Insurance',
          path: '/service/travel-insurance',
          icon: require('../../assets/icon/TravelInsurance.svg').default,
        },
        {
          name: 'Visa',
          path: '/service/visa',
          icon: require('../../assets/icon/Visa.svg').default,
        },
        {
          name: 'Forex',
          path: '/service/forex',
          icon: require('../../assets/icon/Forex.svg').default,
        },
      ],
      hero: {
        title: 'All-in-One Travel Solutions',
        image: '',
        text: 'From flights to insurance, we have everything you need to travel hassle-free.',
        linkText: 'Explore Services',
        linkPath: '/services',
        marginTop: 'mt-2',
      },
    },
    { name: 'Contact Us', path: '/contact-us' },
  ];

  const dropdownWidths = {
    Domestic: 'w-[1000px]',
    International: 'w-[800px]',
    Services: 'w-[800px]',
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (isServicesOpen) setIsServicesOpen(false);
    if (isDomesticOpen) setIsDomesticOpen(false);
    if (isInternationalOpen) setIsInternationalOpen(false);
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
    if (isDomesticOpen) setIsDomesticOpen(false);
    if (isInternationalOpen) setIsInternationalOpen(false);
  };

  const toggleDomestic = () => {
    setIsDomesticOpen(!isDomesticOpen);
    if (isServicesOpen) setIsServicesOpen(false);
    if (isInternationalOpen) setIsInternationalOpen(false);
  };

  const toggleInternational = () => {
    setIsInternationalOpen(!isInternationalOpen);
    if (isServicesOpen) setIsServicesOpen(false);
    if (isDomesticOpen) setIsDomesticOpen(false);
  };

  // NEW Dropdown CSS Classes for Scale-Y Entrance Transition
  const dropdownBaseClasses =
    'invisible absolute left-1/2 top-full z-50 mt-[28px] -translate-x-1/2 transform bg-white p-4 opacity-0 shadow-md transition-all duration-300 ease-out origin-top';
  const dropdownHiddenClasses = 'scale-y-0 translate-y-2';
  const dropdownHoverClasses =
    'group-hover:visible group-hover:opacity-100 group-hover:scale-y-100 group-hover:translate-y-0';

  return (
    <div className='sticky top-0 z-50'>
      {/* Top Bar - Hidden on Mobile */}
      <div className='hidden bg-[#EFFFF8] text-[#0f4c6e] sm:block'>
        <div className='mx-auto flex flex-wrap items-center justify-between px-16 py-2 sm:flex-nowrap'>
          <div className='flex flex-wrap items-center gap-4 sm:flex-nowrap sm:gap-6'>
            <div className='flex items-center gap-2'>
              <BiTimeFive />
              <span className='font-medium'>7 Days - 10:00 AM to 7:00 PM</span>
            </div>
            <div className='flex items-center gap-2 font-normal'>
              <i className='far fa-envelope'></i>
              <a
                className='text-lg hover:text-[#495bff]'
                href='mailto:info@bluelagoontravels.com'
              >
                info@bluelagoontravels.com
              </a>
            </div>
            <div className='flex items-center gap-2 font-normal'>
              <FiPhoneCall />
              <a
                className='hover:text-[#495bff]'
                href='tel:+917573035965'
              >
                +91 7573035965
              </a>
              <span className='text-[#0f4c6e]'>|</span>
              <a
                className='hover:text-[#495bff]'
                href='tel:+919429620999'
              >
                +91 9429620999
              </a>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <a
              aria-label='Instagram'
              className='hover:text-[#495bff]'
              href='https://www.instagram.com/bluelagoon_travels_events/?igsh=NmNwZnZ3OTF0cXNk'
            >
              <BsInstagram
                className='align-middle'
                size={19}
              />
            </a>
            {/* <a
							aria-label='Facebook'
							className='hover:text-[#0a3a56]'
							href='https://www.facebook.com/'
						>
							<FiFacebook
								className='align-middle'
								size={19}
							/>
						</a> */}
          </div>
        </div>
      </div>
      {/* --- */}
      <nav className='relative bg-[#3F5F7D]'>
        <div className='relative mx-auto flex items-center justify-between px-0 py-0'>
          <div className='relative'>
            <svg
              width='319'
              height='89'
              className='fill-[#EFFFF8]'
              viewBox='0 0 319 89'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path d='M0 0H214.972C242.511 0 264.691 22.1036 267 49.5464C268.955 72.7853 319 89 319 89H0V0Z' />
            </svg>
            <Link to='/'>
              <img
                src={Logo}
                alt='Company Logo'
                className='absolute top-1/2 h-20 w-auto -translate-y-1/2 px-4 lg:px-16'
              />
            </Link>
          </div>

          {/* Hamburger Icon for Mobile */}
          <div className='sm:hidden'>
            <button
              onClick={toggleMenu}
              className='mr-4 focus:outline-none'
              aria-label='Toggle menu'
            >
              <div className='flex h-8 w-8 flex-col items-center justify-center gap-2'>
                <span
                  className={`h-1 w-full transform bg-white transition-all duration-300 ease-in-out ${
                    isOpen ? 'translate-y-3.5 rotate-45' : ''
                  }`}
                ></span>
                <span
                  className={`h-1 w-full bg-white transition-all duration-300 ease-in-out ${
                    isOpen ? 'opacity-0' : ''
                  }`}
                ></span>
                <span
                  className={`h-1 w-full transform bg-white transition-all duration-300 ease-in-out ${
                    isOpen ? '-translate-y-3.5 -rotate-45' : ''
                  }`}
                ></span>
              </div>
            </button>
          </div>

          {/* Desktop Menu */}
          <div className='hidden flex-1 items-center justify-center space-x-10 font-normal text-white sm:flex'>
            {navLinks.map((link) => (
              <div
                key={link.path}
                className='group relative'
              >
                <Link
                  to={link.path}
                  className={`flex items-center pb-1 pt-1 transition ${
                    location.pathname === link.path
                      ? 'border-b-2 border-t-2 border-[#EFFFF8] font-semibold text-[#EFFFF8]'
                      : 'hover:text-[#EFFFF8]'
                  }`}
                >
                  {link.name}
                </Link>
                {link.subLinks && (
                  <div
                    className={`${dropdownBaseClasses} ${dropdownHiddenClasses} ${dropdownHoverClasses} ${dropdownWidths[link.name]}`}
                  >
                    <div className='absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 transform bg-white'></div>
                    <div
                      className={`grid gap-4 ${link.name === 'Domestic' ? 'grid-cols-4' : 'grid-cols-3'}`}
                    >
                      <div
                        className={`grid gap-3 ${
                          link.name === 'Domestic'
                            ? 'col-span-3 grid-cols-3'
                            : 'col-span-2 grid-cols-2'
                        } menu-scrollbar max-h-[380px] overflow-y-auto pr-1`}
                      >
                        {link.name === 'Services'
                          ? link.subLinks.map((subLink) => (
                              <Link
                                key={subLink.path}
                                to={subLink.path}
                                className={`flex items-center gap-2 rounded-lg border border-gray-200 p-3 text-gray-700 transition hover:bg-gray-50 hover:text-[#3F5F7D]`}
                              >
                                <div className='flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white group-hover:border-[#3F5F7D]'>
                                  <img
                                    src={subLink.icon}
                                    alt={`${subLink.name} icon`}
                                    className='h-6 w-6'
                                  />
                                </div>
                                <span className='whitespace-nowrap'>{subLink.name}</span>
                              </Link>
                            ))
                          : menuLoading
                            ? Array.from({ length: 6 }).map((_, idx) => (
                                <div
                                  key={idx}
                                  className='flex h-[66px] animate-pulse items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3'
                                >
                                  <div className='h-10 w-10 rounded-full bg-gray-200'></div>
                                  <div className='h-4 w-24 rounded bg-gray-200'></div>
                                </div>
                              ))
                            : link.subLinks.map((subLink) => (
                                <Link
                                  key={subLink.path}
                                  to={subLink.path}
                                  className={`flex items-center gap-2 rounded-lg border border-gray-200 p-3 text-gray-700 transition hover:bg-gray-50 hover:text-[#3F5F7D]`}
                                >
                                  <div className='flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white group-hover:border-[#3F5F7D]'>
                                    <img
                                      src={subLink.icon}
                                      alt={`${subLink.name} icon`}
                                      className='h-6 w-6'
                                    />
                                  </div>
                                  <span className='whitespace-nowrap'>{subLink.name}</span>
                                </Link>
                              ))}
                      </div>
                      {link.hero && (
                        <div className='relative col-span-1 overflow-hidden rounded-lg bg-gradient-to-t from-[#55e6a5] to-[#495bff]'>
                          {/* {link.hero.image && (
														<img
															src={link.hero.image}
															alt={link.hero.title}
															className={`relative z-10 w-full object-cover ${link.hero.imageHeight}`}
														/>
													)} */}
                          <div className='absolute inset-0 left-3 z-20'></div>
                          <div
                            className={`align-center absolute z-30 grid justify-center p-3 text-white ${link.hero.marginTop}`}
                          >
                            <h4 className='text-md mont text-center text-2xl font-semibold'>
                              {link.hero.title}
                            </h4>
                            <p className='mt-3'>{link.hero.text}</p>
                            <Link
                              to={link.hero.linkPath}
                              className='mt-3 inline-block w-full rounded-lg bg-[#EFFFF8] px-4 py-1 text-center text-sm font-bold text-[#3F5F7D] transition-colors duration-300 hover:text-[#495BFF]'
                            >
                              {link.hero.linkText}
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={handleOpen}
            className='header-button mr-4 hidden items-center sm:flex'
          >
            <span className='header-button__icon-wrapper'>
              <GiCommercialAirplane className='header-button__icon-svg' />
              <GiCommercialAirplane className='header-button__icon-svg header-button__icon-svg--copy' />
            </span>
            Plan Your Trip Today
          </button>

          {/* Modal */}
          <BookNowModal
            open={modalOpen}
            onClose={handleClose}
          />
        </div>

        {/* Mobile Sidebar */}
        <div
          className={`fixed left-0 top-0 h-full w-full transform bg-white text-[#3F5F7D] transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          } sm:hidden`}
        >
          <div className='flex items-center justify-between border-b p-4'>
            <Link to='/'>
              <img
                src={Logo}
                alt='Company Logo'
                className='h-14 w-auto'
              />
            </Link>
            <button
              onClick={toggleMenu}
              className='focus:outline-none'
              aria-label='Close menu'
            >
              <IoClose className='text-2xl text-[#3F5F7D]' />
            </button>
          </div>
          <div className='flex flex-col space-y-2 p-4'>
            {navLinks.map((link) => (
              <div key={link.path}>
                {link.subLinks ? (
                  <>
                    <div className='flex w-full items-center justify-between'>
                      <Link
                        to={link.path}
                        onClick={toggleMenu}
                        className={`flex-1 text-lg ${
                          location.pathname === link.path
                            ? 'font-semibold text-[#3F5F7D]'
                            : 'hover:text-[#3F5F7D]'
                        }`}
                      >
                        {link.name}
                      </Link>
                      <button
                        onClick={
                          link.name === 'Services'
                            ? toggleServices
                            : link.name === 'Domestic'
                              ? toggleDomestic
                              : toggleInternational
                        }
                        className='p-2 focus:outline-none'
                        aria-label={`Toggle ${link.name} submenu`}
                      >
                        <IoChevronDown
                          className={`rounded-[2px] bg-[#3F5F7D] text-[16px] text-white transition-transform duration-300 ease-in-out ${
                            (link.name === 'Services' && isServicesOpen) ||
                            (link.name === 'Domestic' && isDomesticOpen) ||
                            (link.name === 'International' && isInternationalOpen)
                              ? 'rotate-180'
                              : ''
                          }`}
                        />
                      </button>
                    </div>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        (link.name === 'Services' && isServicesOpen) ||
                        (link.name === 'Domestic' && isDomesticOpen) ||
                        (link.name === 'International' && isInternationalOpen)
                          ? 'menu-scrollbar max-h-[380px] overflow-y-auto'
                          : 'max-h-0'
                      }`}
                    >
                      {link.name === 'Services'
                        ? link.subLinks.map((subLink) => (
                            <Link
                              key={subLink.path}
                              to={subLink.path}
                              onClick={toggleMenu}
                              className={`flex items-center py-2 pl-4 ${
                                location.pathname === subLink.path
                                  ? 'font-semibold text-[#3F5F7D]'
                                  : 'hover:text-[#3F5F7D]'
                              }`}
                            >
                              {subLink.icon && (
                                <img
                                  src={subLink.icon}
                                  alt={`${subLink.name} icon`}
                                  className='mr-2 h-4 w-4'
                                />
                              )}
                              {subLink.name}
                            </Link>
                          ))
                        : menuLoading
                          ? Array.from({ length: 3 }).map((_, idx) => (
                              <div
                                key={idx}
                                className='flex animate-pulse items-center py-2.5 pl-4'
                              >
                                <div className='mr-2 h-4 w-4 rounded-full bg-gray-200'></div>
                                <div className='h-4 w-24 rounded bg-gray-200'></div>
                              </div>
                            ))
                          : link.subLinks.map((subLink) => (
                              <Link
                                key={subLink.path}
                                to={subLink.path}
                                onClick={toggleMenu}
                                className={`flex items-center py-2 pl-4 ${
                                  location.pathname === subLink.path
                                    ? 'font-semibold text-[#3F5F7D]'
                                    : 'hover:text-[#3F5F7D]'
                                }`}
                              >
                                {subLink.icon && (
                                  <img
                                    src={subLink.icon}
                                    alt={`${subLink.name} icon`}
                                    className='mr-2 h-4 w-4'
                                  />
                                )}
                                {subLink.name}
                              </Link>
                            ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    onClick={toggleMenu}
                    className={`text-lg ${
                      location.pathname === link.path
                        ? 'font-semibold text-[#3F5F7D]'
                        : 'hover:text-[#3F5F7D]'
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <div className='flex flex-col space-y-4 pt-4'>
              <div className='flex items-center gap-1 font-semibold'>
                <i className='far fa-envelope'></i>
                <a
                  className='hover:underline'
                  href='mailto:info@bluelagoontravels.com'
                >
                  info@bluelagoontravels.com
                </a>
              </div>
              <div className='flex items-center gap-1 font-semibold'>
                <FiPhoneCall />
                <a
                  className='hover:underline'
                  href='tel:+917573035965'
                >
                  +91 7573035965
                </a>
              </div>
              <div className='flex items-center gap-1 font-semibold'>
                <FiPhoneCall />
                <a
                  className='hover:underline'
                  href='tel:+919429620999'
                >
                  +91 9429620999
                </a>
              </div>
              <div className='flex items-center gap-4'>
                <a
                  aria-label='Instagram'
                  className='hover:text-[#495bff]'
                  href='https://www.instagram.com/bluelagoon_travels_events/?igsh=NmNwZnZ3OTF0cXNk'
                >
                  <BsInstagram className='align-middle' />
                </a>
                {/* <a
									aria-label='Facebook'
									className='hover:text-[#3F5F7D]'
									href='https://www.facebook.com/'
								>
									<FiFacebook className='align-middle' />
								</a> */}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
